import type {BeatPlanningCalendarEvent} from "~/components/beatPlanningCalendar/beatPlanningCalendar";

export const CALENDAR_EVENT_COUNT_SUMMARY_FLAG = "isEventCountSummary";
export const CALENDAR_EVENT_COUNT_SUMMARY_SOURCE_KEY = "summarySourceEvents";
export const CALENDAR_EVENT_COUNT_SUMMARY_CELL_KEY = "summaryCellEvents";

/** Visible event chips before a `+N` overflow control (month day cells and time-grid hour cells). */
export const CALENDAR_MAX_VISIBLE_STACKED_EVENTS = 5;

function toLocalDayKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/** One time-grid hour cell: same local day + hour (matches FullCalendar 1h slots). */
export function getTimeGridCellKey(start: Date): string {
    return `${toLocalDayKey(start)}-h${start.getHours()}`;
}

function sortEventsByStart(events: Array<BeatPlanningCalendarEvent>): Array<BeatPlanningCalendarEvent> {
    return [...events].sort(
        (left, right) => new Date(left.start).getTime() - new Date(right.start).getTime(),
    );
}

/** Place the +N badge at the bottom of the matching 1h time-grid slot. */
function getSummaryAnchorAtHourCellBottom(referenceStart: Date): [Date, Date] {
    const start = new Date(referenceStart);
    start.setMinutes(59, 0, 0);
    const end = new Date(start);
    end.setMinutes(59, 59, 999);
    return [start, end];
}

function collapseEventGroup(
    group: Array<BeatPlanningCalendarEvent>,
    maxVisible: number,
    summaryIdKey: string,
): Array<BeatPlanningCalendarEvent> {
    if (group.length <= maxVisible) {
        return group;
    }

    const sorted = sortEventsByStart(group);
    const visible = sorted.slice(0, maxVisible);
    const overflow = sorted.slice(maxVisible);
    const isAllDayGroup = summaryIdKey.startsWith("allday-");
    const [summaryStart, summaryEnd] =
        isAllDayGroup === true
            ? (() => {
                  const anchor = visible[visible.length - 1] ?? overflow[0]!;
                  const start = new Date(anchor.start);
                  return [start, new Date(start.getTime() + 60_000)] as [Date, Date];
              })()
            : getSummaryAnchorAtHourCellBottom(new Date(sorted[0]!.start));

    return [
        ...visible,
        {
            id: `__event-count__-${summaryIdKey}-${overflow.length}`,
            title: `+${overflow.length}`,
            start: summaryStart,
            end: summaryEnd,
            allDay: isAllDayGroup,
            extendedProps: {
                isEventCountSummary: true,
                summarySourceEvents: overflow,
                summaryCellEvents: sorted,
            },
        },
    ];
}

/**
 * Caps visible events per time-grid hour cell (and per day in the all-day row); remainder as `+N`.
 */
export function collapseTimedEventsToCountSummaries(
    events: Array<BeatPlanningCalendarEvent>,
    maxVisible: number = CALENDAR_MAX_VISIBLE_STACKED_EVENTS,
): Array<BeatPlanningCalendarEvent> {
    const timedByCell = new Map<string, Array<BeatPlanningCalendarEvent>>();
    const allDayByDay = new Map<string, Array<BeatPlanningCalendarEvent>>();

    for (const event of events) {
        const start = new Date(event.start);
        if (Number.isNaN(start.getTime())) {
            continue;
        }
        if (event.allDay === true) {
            const dayKey = toLocalDayKey(start);
            const bucket = allDayByDay.get(dayKey) ?? [];
            bucket.push(event);
            allDayByDay.set(dayKey, bucket);
            continue;
        }
        const cellKey = getTimeGridCellKey(start);
        const bucket = timedByCell.get(cellKey) ?? [];
        bucket.push(event);
        timedByCell.set(cellKey, bucket);
    }

    const collapsed: Array<BeatPlanningCalendarEvent> = [];

    for (const [dayKey, group] of allDayByDay) {
        collapsed.push(...collapseEventGroup(group, maxVisible, `allday-${dayKey}`));
    }
    for (const [cellKey, group] of timedByCell) {
        collapsed.push(...collapseEventGroup(group, maxVisible, cellKey));
    }

    return collapsed;
}

export function getCalendarEventCountSummarySources(
    extendedProps: unknown,
): Array<BeatPlanningCalendarEvent> | undefined {
    if (extendedProps === null || typeof extendedProps !== "object") {
        return undefined;
    }
    const record = extendedProps as Record<string, unknown>;
    if (record.isEventCountSummary !== true && record[CALENDAR_EVENT_COUNT_SUMMARY_FLAG] !== true) {
        return undefined;
    }
    const sources = record.summarySourceEvents ?? record[CALENDAR_EVENT_COUNT_SUMMARY_SOURCE_KEY];
    if (!Array.isArray(sources)) {
        return undefined;
    }
    return sources as Array<BeatPlanningCalendarEvent>;
}

export function getCalendarEventCountSummaryCellEvents(
    extendedProps: unknown,
): Array<BeatPlanningCalendarEvent> | undefined {
    if (extendedProps === null || typeof extendedProps !== "object") {
        return undefined;
    }
    const record = extendedProps as Record<string, unknown>;
    const cellEvents = record.summaryCellEvents ?? record[CALENDAR_EVENT_COUNT_SUMMARY_CELL_KEY];
    if (!Array.isArray(cellEvents)) {
        return undefined;
    }
    return cellEvents as Array<BeatPlanningCalendarEvent>;
}
